/**
 * App state: the domain store (framework-free, mirrored into Vue through `version`)
 * plus the UI state the prototype kept on `ctx` (step, selection, drafts, analysis, jobs…).
 * One studio per page, so these are module singletons.
 */
import { computed, markRaw, reactive, ref, shallowRef } from 'vue';
import type { BuildModel, ConstructionProfile, Design, IntakeProposal, Store, CheckReceipt } from '@/domain';
import { createStore, isProvisional, isSelfAuthored } from '@/domain';
import type { FitCandidate, FitResult } from '@/families/fit-schema';
import { familyOf } from '@/families/registry';
import { resolveStock, type AnyStock } from '@/families/stock';
import type { BookcaseViewer } from '@/viewer';

export const STEPS = [
  { id: 'reference', label: 'Reference' },
  { id: 'check', label: 'Check' },
  { id: 'shape', label: 'Shape' },
  { id: 'look', label: 'Look' },
  { id: 'pack', label: 'Pack' },
] as const;
export type StepId = (typeof STEPS)[number]['id'];
export const isStep = (id: string): id is StepId => STEPS.some((s) => s.id === id);

export interface RefImage {
  id: string;
  src: string;
  thumb: string;
  alt: string;
  sha256: string;
  rightsBasis: string;
  path?: string;
  width?: number;
  height?: number;
}
export interface ReferenceState { url: string; text: string; images: RefImage[] }

export type AnalysisStatus = 'idle' | 'running' | 'review' | 'done' | 'manual' | 'refused' | 'error';
export interface AnalysisState {
  status: AnalysisStatus;
  stage?: string;
  stageIndex?: number;
  stageLabel?: string;
  /** The service's "still analysing" notice for a long run (SSE `notice`). */
  notice?: string;
  ranAt?: string | null;
  proposalAt?: string | null;
  /** The analysis result: a FitResult, or a first-slice IntakeProposal in older projects. */
  proposal?: FitResult | IntakeProposal | null;
  /** The fit decision (plan §4.3): chosen family, confidence, flagged (0.40–0.69), runners-up. */
  fit?: { familyId: string; confidence: number; flagged: boolean; runnersUp: FitCandidate[] } | null;
  refusal?: { reason: string; detected?: { family: string; bays: number }; candidates?: FitCandidate[] } | null;
  error?: string;
  code?: string;
  mode?: 'replace' | 'new';
  runId?: string;
  rawResponsePath?: string | null;
  usage?: Record<string, number> | null;
}

export interface ChoiceOption { label: string; sub?: string; primary?: boolean; act: () => void }
export interface ChoiceState { title: string; lede: string; choices: ChoiceOption[] }
export interface ToastItem { id: number; msg: string; action?: string; onAction?: () => void; tone?: string }
export type Theme = 'system' | 'light' | 'dark';
export type SheetMode = 'peek' | 'half' | 'full';

/** Holders for non-reactive objects (the store and the viewer). */
export const app = {
  store: markRaw(createStore()) as Store,
  stock: null as AnyStock | null,
  construction: null as ConstructionProfile | null,
};
export const viewerRef = shallowRef<BookcaseViewer | null>(null);
export const offscreenViewerRef = shallowRef<BookcaseViewer | null>(null);
/** A shelf-handle drag preview (not yet in the store). */
export const stagePreview = shallowRef<{ design: Design; build: BuildModel } | null>(null);

/** Bumped on every store event so Vue re-reads the store. */
export const version = ref(0);
let unsub: (() => void) | null = null;
const storeListeners = new Set<Parameters<Store['subscribe']>[0]>();
export function onStoreEvent(fn: Parameters<Store['subscribe']>[0]): () => void {
  storeListeners.add(fn);
  return () => storeListeners.delete(fn);
}
export function setStore(store: Store): void {
  unsub?.();
  app.store = markRaw(store);
  ui.frontSelection = [];
  ui.frontPicking = false;
  unsub = store.subscribe((s, e) => {
    version.value++;
    for (const fn of storeListeners) {
      try { fn(s, e); } catch (err) { console.error(err); }
    }
  });
  version.value++;
}

/** A reactive view of the store (re-read on every store event). */
export const snap = computed(() => {
  void version.value;
  const s = app.store;
  return {
    design: s.design,
    build: s.build,
    fields: s.fields,
    revisions: s.revisions,
    exports: s.exports,
    currentRevisionId: s.currentRevisionId,
    currentRevision: s.currentRevision,
    nextRevisionId: s.nextRevisionId,
    isDirty: s.isDirty,
    canUndo: s.canUndo,
    canRedo: s.canRedo,
    undoLabel: s.undoLabel,
    redoLabel: s.redoLabel,
    viewing: s.viewing,
    lastEditMs: s.lastEditMs,
    stock: s.stock,
    // other families (design.params) build from their own stock and joint profile
    stockInfo: resolveStock(s.design.params ? (s.build.stock as AnyStock) : s.stock),
    construction: s.design.params ? s.build.construction : s.construction,
    provisional: isProvisional(s.design.params ? s.build.construction : s.construction),
    selfAuthored: isSelfAuthored(s.design.params ? s.build.construction : s.construction),
    family: familyOf(s.design).definition,
  };
});

export const ui = reactive({
  booted: false,
  bootError: null as string | null,
  service: { intakeMode: '', version: '', demo: false },
  projectId: null as string | null,
  step: 'reference' as StepId,
  sel: null as string | null,
  hov: null as string | null,
  openingSel: null as number | null,
  frontPicking: false,
  frontSelection: [] as string[],
  ref: { url: '', text: '', images: [] } as ReferenceState,
  analysis: { status: 'idle' } as AnalysisState,
  empty: true,
  fieldErrors: {} as Record<string, string>,
  drafts: {} as Record<string, string>,
  checkEditing: null as string | null,
  rebalance: 'neighbour' as 'neighbour' | 'none',
  /** Shape: the bay whose shelves and openings are being edited (0-based). */
  bay: 0,
  /** Look: Inspect (the kit's detailed pieces on the live model, with part picking; no Blender). */
  inspect: false,
  /** Look: Explode (parts pulled apart), separate from Inspect (FND-15, ui-spec §2.4). */
  explode: false,
  /** Look: doors and drawers shown open (viewer only). */
  frontsOpen: false,
  /** A material (stock) change that made a valid design invalid: what it was before, for "Keep …" (run 2, p09/p11). */
  stockBreak: null as { from: string; to: string } | null,
  /** Look has been opened in this project (rail state "Looked"). */
  looked: false,
  theme: 'system' as Theme,
  effectiveTheme: 'light' as 'light' | 'dark',
  sheet: 'half' as SheetMode,
  savedAt: null as string | null,
  saveError: null as string | null,
  restoreOffer: null as { savedAt: string } | null,
  litLines: [] as string[],
  litFact: null as string | null,
  menuOpen: false,
  choice: null as ChoiceState | null,
  historyOpen: false,
  pickerOpen: false,
  exportSheetOpen: false,
  lightbox: null as { kind: 'ref' } | null,
  toasts: [] as ToastItem[],
  stepIn: false,
  view: { view: 'three-quarter' as string, dims: false, figure: true },
  print: null as { revisionId: string | null; dirty: boolean; hero: string | null; at: string } | null,
  /** Independent check receipts for this project (service-owned), and the revision being checked now. */
  checks: [] as CheckReceipt[],
  checking: null as string | null,
  /** Look: the last saved picture (object URL) for the toast and tests. */
  picture: null as { filename: string; bytes: number } | null,
  /** Simulation fixtures (test hooks only; the real failures come from the service). */
  editMs: '',
});

/** The build drawn on the stage: a viewed revision, or the live design. */
const viewedCache = new Map<string, BuildModel>();
export const shownBuild = computed<BuildModel>(() => {
  const s = snap.value;
  if (s.viewing) {
    const r = app.store.revision(s.viewing);
    if (r) {
      const key = `${r.id}:${r.manufacturingHash}:${r.appearanceHash}`;
      let b = viewedCache.get(key);
      if (!b) { b = markRaw(familyOf(r.design).generate(r.design, app.store.stock, s.construction)); viewedCache.set(key, b); }
      return b;
    }
  }
  return s.build;
});

export type { ConstructionProfile, Design };
